<template>
  <fragment>
    <div class="overviewTitle">
      <h2 class="mb-6">{{ $t("overview.productionUnit.title") }}</h2>
      <div v-if="!isFullScreen" class="PU-list" :class="[clickedChildState ? 'child-clicked' : false]">
        <template v-for="(pu, index) in productionUnitsStateKpisAndAlert">
          <p-u-item
            :key="'overviewGridPu-' + index"
            :pu-id="pu.puId"
            :pu-name="pu.puName"
            :pu-status="pu.puStatus"
            :product-sku="pu.productSku"
            :product-name="pu.productName"
            :kpi-selected="pu.kpiSelected"
            :oee-value="pu.oeeValue"
            :oee-target="pu.oeeTarget"
            :ooe-value="pu.ooeValue"
            :availability-value="pu.availabilityValue"
            :availability-target="pu.availabilityTarget"
            :performance-value="pu.performanceValue"
            :performance-target="pu.performanceTarget"
            :quality-value="pu.qualityValue"
            :quality-target="pu.qualityTarget"
            :all-product-quantity-value="pu.allProductQuantityValue"
            :current-product-quantity-value="pu.currentProductQuantityValue"
            :rate-per-hour-value="pu.ratePerHourValue"
            :rate-per-hour-target="pu.ratePerHourTargetValue"
            :rate-per-minute-value="pu.ratePerMinuteValue"
            :rate-per-minute-target="pu.ratePerMinuteTargetValue"
            :speed-five-minute-value="pu.speedFiveMinuteValue"
            :speed-five-minute-value-per-minute="pu.speedFiveMinuteValuePerMinute"
            :is-disconnected="pu.isDisconnected || false"
            :is-disconnected-since="pu.isDisconnectedSince"
            :index="index"
            :details-active="index === detailsIndex"
            :is-full-screen="isFullScreen"
            @toggleDetails="toggleDetailsByIndex"
            @closeDetails="closeDetails"
          />

          <p-u-details
            v-if="index === detailsIndex"
            :key="'overviewPuDetails-' + index"
            :name="pu.puName"
            :production-unit-id="pu.puId"
            @closeDetails="closeDetails"
          />
        </template>
      </div>
      <v-carousel
        v-else
        v-model="carouselIndex"
        continuous
        cycle
        interval="15000"
        hide-delimiter-background
        :show-arrows="false"
        height="calc(100vh - 200px)"
      >
        <v-carousel-item
          v-for="(window, index) in carouselProductionUnitsStateKpisAndAlert"
          :key="`carousel-item-${index}`"
        >
          <v-row style="height: 100%">
            <v-col
              cols="12"
              xs="12"
              sm="6"
              md="4"
              lg="3"
              v-for="(pu, index) in window"
              :key="'overviewGridPu-' + index"
            >
              <p-u-item
                :pu-id="pu.puId"
                :pu-name="pu.puName"
                :pu-status="pu.puStatus"
                :product-sku="pu.productSku"
                :product-name="pu.productName"
                :kpi-selected="pu.kpiSelected"
                :oee-value="pu.oeeValue"
                :oee-target="pu.oeeTarget"
                :ooe-value="pu.ooeValue"
                :availability-value="pu.availabilityValue"
                :availability-target="pu.availabilityTarget"
                :performance-value="pu.performanceValue"
                :performance-target="pu.performanceTarget"
                :quality-value="pu.qualityValue"
                :quality-target="pu.qualityTarget"
                :all-product-quantity-value="pu.allProductQuantityValue"
                :current-product-quantity-value="pu.currentProductQuantityValue"
                :rate-per-hour-value="pu.ratePerHourValue"
                :rate-per-hour-target="pu.ratePerHourTargetValue"
                :rate-per-minute-value="pu.ratePerMinuteValue"
                :rate-per-minute-target="pu.ratePerMinuteTargetValue"
                :speed-five-minute-value="pu.speedFiveMinuteValue"
                :speed-five-minute-value-per-minute="pu.speedFiveMinuteValuePerMinute"
                :current-production-run-quantity-value="pu.currentProductionRunQuantityValue"
                :is-disconnected="pu.isDisconnected || false"
                :is-disconnected-since="pu.isDisconnectedSince"
                :index="index"
                :is-full-screen="isFullScreen"
                @toggleDetails="toggleDetailsByIndex"
                @closeDetails="closeDetails"
              />
            </v-col>
          </v-row>
        </v-carousel-item>
      </v-carousel>
    </div>
  </fragment>
</template>

<script>
import PUItem from "@/components/overview/PUItem";
import PUDetails from "@/components/overview/PUDetails";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "PUList",
  components: { PUItem, PUDetails },
  props: {
    isFullScreen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      detailsIndex: -1,
      clickedChildState: false,
      carouselIndex: 0,
    };
  },
  computed: {
    ...mapGetters("overview", [
      "productionUnitsStateAndKpis",
      "productionUnitsSpeed",
      "activeProductionUnitIdForDetails",
    ]),
    ...mapGetters("navigation", ["activeFactory", "activeFactoryDataSourceAlerts"]),
    maxWindowSize() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 2;
        case "sm":
          return 4;
        case "md":
          return 6;
        default:
          return 8;
      }
    },
    carouselProductionUnitsStateKpisAndAlert() {
      if (!this.isFullScreen) return [];
      const items = this.productionUnitsStateKpisAndAlert;
      const maxWindowSize = this.maxWindowSize;

      let windows = [];
      let currentWindow = [];

      for (let i = 0; i < items.length; i++) {
        if (currentWindow.length >= maxWindowSize) {
          windows.push(currentWindow);
          currentWindow = [];
        }
        currentWindow.push(items[i]);
      }

      if (currentWindow.length > 0) {
        windows.push(currentWindow);
      }
      return windows;
    },
    productionUnitsStateKpisAndAlert() {
      if (this.activeFactory) {
        let alertsByPU = new Map();
        this.activeFactoryDataSourceAlerts
          .filter((a) => a.factory_id === this.activeFactory.id)
          .forEach((a) => {
            a.production_unit_ids.forEach((puId) => {
              let puAlerts = alertsByPU.get(puId);
              if (puAlerts) {
                puAlerts.push(a);
              } else {
                puAlerts = [];
                puAlerts.push(a);
                alertsByPU.set(puId, puAlerts);
              }
            });
          });

        this.productionUnitsStateAndKpis.forEach((puStateKpis) => {
          // Alert
          puStateKpis.isDisconnected = false;
          puStateKpis.isDisconnectedSince = null;
          const alerts = alertsByPU.get(puStateKpis.puId);
          if (alerts && alerts.length > 0) {
            const inactiveSinceValues = alerts.map((a) => a.inactive_since);
            puStateKpis.isDisconnected = true;
            puStateKpis.isDisconnectedSince = Math.min(...inactiveSinceValues);
          }

          // Speed
          puStateKpis.speedFiveMinuteValue = null;
          puStateKpis.speedFiveMinuteValuePerMinute = null;
          const speedForPU = this.productionUnitsSpeed.find((speedForPU) => speedForPU.production_unit_id === puStateKpis.puId);
          if (speedForPU) {
            const speedInUnitsPerHour = speedForPU.product_speed;
            puStateKpis.speedFiveMinuteValue = speedForPU.product_speed;
            puStateKpis.speedFiveMinuteValuePerMinute = speedInUnitsPerHour ? speedInUnitsPerHour / 60.0 : null;
          }
        });
      }
      return this.productionUnitsStateAndKpis;
    },
  },
  methods: {
    ...mapActions("overview", ["clearProductionUnitIdForDetails", "setActiveProductionUnitIdForDetails"]),
    toggleDetailsByIndex(puIdAndIndex) {
      if (this.detailsIndex === puIdAndIndex.index) {
        this.closeDetails();
      } else {
        this.detailsIndex = puIdAndIndex.index;
        this.clickedChildState = true;
        this.setActiveProductionUnitIdForDetails(puIdAndIndex.production_unit_id);
      }
    },
    closeDetails() {
      this.clearProductionUnitIdForDetails();
      this.clickedChildState = false;
      this.detailsIndex = -1;
    },
  },
  mounted() {
    let puIndex = -1;
    this.productionUnitsStateKpisAndAlert.find((pu, index) => {
      if (pu.puId === this.activeProductionUnitIdForDetails) {
        puIndex = index;
        return true;
      } else {
        return false;
      }
    });
    this.detailsIndex = puIndex;
  },
};
</script>

<style lang="scss" scoped>
.overviewTitle {
  padding-top: 20px;
}
.PU-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: var(--grid-gutter);
  grid-auto-flow: row dense;
  padding-left: 7px; // spacer for shadow from elevation props
  padding-bottom: var(--grid-gutter);

  &.clicked {
    border: 2px solid red;
  }
  @media ($wx-sm-min) {
    grid-template-columns: repeat(3, 1fr);
    grid-gap: calc(var(--grid-gutter) / 2);
  }

  @media ($wx-md-min) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media ($wx-lg-min) {
    grid-template-columns: repeat(5, 1fr);
  }

  @media ($wx-xl-min) {
    grid-template-columns: repeat(6, 1fr);
  }

  /**
  * Dimming the visibility of the unactive
  * siblings of a `pu-cards.details-active`
  * from PUItems.vue
  */
  article {
    opacity: 1;
    transition: var(--smooth-transition);
  }

  &.child-clicked {
    article:not(.disconnected) {
      // all cards that are not clicked
      &:not(.details-active) {
        opacity: var(--opacity-disabled-effect);
      }
      // Un mouse hover users can better read the unactivated card
      &:hover {
        opacity: 1 !important;
      }
    }
  }
}
</style>
